import {
    Component,
    OnInit,
    OnDestroy, ViewChild, Input, EventEmitter, Output,
} from '@angular/core';
import {FormGroup, FormControl, FormArray} from '@angular/forms';

import {
    BehaviorSubject,
    Subscription,
    of,
    map,
    switchMap,
    take,
    combineLatest,
    Observable,
    isObservable,
    distinctUntilChanged, firstValueFrom
} from 'rxjs';

import {ConfirmationService, SelectItem} from 'primeng/api';

import { OutboundLoadService } from '../../../../services/outbound-load.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import {CustomerService} from '@shared/services/customer.service';

import {CarrierService} from "@shared/services/carrier.service";
import {ProductService} from "../../../../services/product.service";
import {tap} from "rxjs/operators";
import {SiteConfig} from "@shared/models/site-config";
import {environment} from "@environment";
import {
    OutboundLoad,
} from "../../../../models/outbound-load";
import {
    OutboundLoadTemplateSharedContext,
    OutboundLoadTemplate,
    OutboundLoadTemplateLocalContext,
    OutboundLoadTemplateDefinition,
    OutboundLoadTemplateFormControls
} from "./templates/base";
import {WalmartReuseTemplate} from "./templates/walmart-reuse";
import {DateTime} from "luxon";
import {MessageService} from "@shared/services/message-service";
import {OverlayPanel} from "primeng/overlaypanel";
import {BulkCreateResult} from "../../../../models/generic/bulk-create-result";
import {fromFormControl} from "@shared/helpers/from-form-control";
import {CustomerSite} from "@shared/models/customer-site";
import {BolService} from "../../../../services/bol.service";

interface OutboundLoadRow {
    formGroup: FormGroup<OutboundLoadTemplateFormControls>;
    localContext: BehaviorSubject<OutboundLoadTemplateLocalContext>;
    subscriptions: Subscription[];
    result: BulkCreateResult<OutboundLoad, string>;
}

@Component({
    selector: 'app-create-outbound-load-from-template',
    templateUrl: './create-outbound-load-from-template.component.html',
    styleUrls: ['./create-outbound-load-from-template.component.scss']
})
export class CreateOutboundLoadFromTemplateComponent implements OnInit, OnDestroy {
    @Input() show: boolean = false;
    @Output() onClose = new EventEmitter<void>();

    selectedTemplateDefinition: OutboundLoadTemplateDefinition | null = null;
    selectedTemplate: OutboundLoadTemplate | null = null;
    templateOptions: SelectItem<OutboundLoadTemplateDefinition>[] = [];
    sharedTemplateContext = new BehaviorSubject<OutboundLoadTemplateSharedContext | null>(null);

    loading: boolean = false;
    rows: OutboundLoadRow[] = [];

    private subscriptions: Subscription[] = [];

    readonly maxClones: number = 30;
    cloningRowIndex: number | null = null;
    @ViewChild('clonePanel') clonePanel: OverlayPanel;

    constructor(
        private shiftContextService: ShiftContextService,
        private carrierService: CarrierService,
        private productService: ProductService,
        private customerService: CustomerService,
        private outboundLoudService: OutboundLoadService,
        private messageService: MessageService,
        private bolService: BolService,
        private confirmationService: ConfirmationService
    ) {

    }

    handleClose(event: Event): void {
        const uncreatedRows = this.rows.filter(row => !this.hasCreated(row));
        // NOTE(nicholas-ramsey): This is just to check if it's the initial row. In which case, if it isn't filled out,
        // we probably don't need to bother them with confirmation.
        const hasSingleInvalidRow = uncreatedRows.length === 1 && !uncreatedRows[0].formGroup.valid;

        if(uncreatedRows.length > 0 && !hasSingleInvalidRow) {
            this.confirmationService.confirm({
                header: 'Unsaved Loads',
                message: `You have <span class="p-text-bold">${uncreatedRows.length} unsaved load(s)</span>. Are you sure you want to close?`,
                accept: () => {
                    this.onClose.emit();
                },
                acceptLabel: 'Close Without Saving',
                acceptButtonStyleClass: 'p-button-danger',
                rejectLabel: 'Cancel',
                rejectButtonStyleClass: 'p-button-secondary',
            });

            event.stopPropagation();

            return;
        }

        this.onClose.emit();
    }

    get invalid(): boolean {
        return this.rows.length === 0 || !this.rows.filter(row => !this.hasCreated(row)).every(row => row.formGroup.valid);
    }

    ngOnInit() {
        const templates = [
            new WalmartReuseTemplate(
                this.carrierService,
                this.customerService,
                this.outboundLoudService
            )
        ];

        this.templateOptions = templates.map(template => {
            return {
                label: template.label,
                value: template
            };
        });

        this.subscriptions = [
            this.shiftContextService.CurrentContext.pipe(
                switchMap(shiftContext => {
                    const siteTimezone = this.shiftContextService.SiteConfig.pipe(
                        map(configs =>
                            configs.find(c => c.configKey === SiteConfig.SITE_TIME_ZONE)?.GetValue<string>()
                            ?? environment.fallbackTimeZone
                        )
                    );

                    return combineLatest([
                        of(shiftContext),
                        this.carrierService.getCarriers(),
                        this.customerService.getCustomers(shiftContext.site.id, shiftContext.date, "outbound"),
                        siteTimezone
                    ]);
                })
            ).subscribe(([shiftContext, carriers, customerAssignments, siteTimezone]) => {
                this.sharedTemplateContext.next({
                    carriers: carriers.filter(c => c.activeFlag),
                    shiftContext,
                    customerAssignments,
                    siteSubInventories: shiftContext.site.subinventories.filter(subInv => subInv.isActive),
                    siteTimezone: siteTimezone
                });

                this.resetForm();
            }),
        ];
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
        this.selectedTemplateDefinition?.unsubscribe();

        this.rows.forEach(row => {
            row.subscriptions.forEach(s => s.unsubscribe());
            row.localContext.unsubscribe();
        });
    }

    private setTemplateContext(row: OutboundLoadRow, context: OutboundLoadTemplateLocalContext) {
        if (this.selectedTemplateDefinition?.transformContext) {
            context = this.selectedTemplateDefinition.transformContext(context);
        }

        row.localContext.next(context);
    }

    onTemplateDefinitionChange(newDefinition: OutboundLoadTemplateDefinition | null) {
        this.selectedTemplateDefinition?.unsubscribe();

        this.selectedTemplateDefinition = newDefinition;
        this.resetForm();
        this.addFormGroup().subscribe({
            next: () => {}
        });
    }

    resetForm() {
        this.selectedTemplate = this.selectedTemplateDefinition?.template ?? null;
        this.rows.forEach(row => {
            row.subscriptions.forEach(s => s.unsubscribe());
            row.localContext.unsubscribe();
        });
        this.rows = [];
    }

    private buildLoadFormGroup(template: OutboundLoadTemplate): Observable<FormGroup> {
        const group = this.selectedTemplateDefinition.toFormGroup();
        const defaultValueObservables: Observable<unknown>[] = [];

        Object.keys(template).forEach(key => {
            const field = template[key];
            const defaultValue = field.defaultValueFn ? field.defaultValueFn(this.sharedTemplateContext.value) : null;

            const control = group.get(key) as FormControl<unknown>;

            if (isObservable(defaultValue)) {
                defaultValueObservables.push(
                    defaultValue.pipe(
                        take(1),
                        map(value => ({ key, value }))
                    )
                );
            } else {
                control.setValue(defaultValue);
            }

            if (!field.isVisible) {
                control.disable({ onlySelf: true });
            }
        });

        if (defaultValueObservables.length > 0) {
            return combineLatest(defaultValueObservables).pipe(
                map(results => {
                    results.forEach(({ key, value }) => {
                        const control = group.get(key);
                        if (control) {
                            control.setValue(value);
                        }
                    });
                    return group;
                })
            );
        }

        return of(group);
    }

    private setUpRowSubscriptions(row: OutboundLoadRow) {
        const siteSubInvControl = row.formGroup.get('siteSubinventory');
        const poolerControl = row.formGroup.get('pooler');
        const customerControl = row.formGroup.get('invoiceTo');
        const shipToLocationControl = row.formGroup.get('shipToLocation');

        row.subscriptions = [
            combineLatest([
                fromFormControl(siteSubInvControl),
                this.sharedTemplateContext
            ])
            .subscribe(([siteSubInv, sharedTemplateContext]) => {
                if (!sharedTemplateContext || !siteSubInv) {
                    return;
                }

                const customerIds = sharedTemplateContext.customerAssignments.assignments
                    .filter(a => a.siteSubinventoryId === siteSubInv.id)
                    .map(a => a.customerId);

                this.setTemplateContext(row, {
                    ...row.localContext.value,
                    customers: sharedTemplateContext.customerAssignments.customers.filter(
                        c => c.isActive && c.isPrimary && customerIds.includes(c.id)
                    )
                });
            }),
            combineLatest([
                fromFormControl(siteSubInvControl),
                fromFormControl(customerControl),
                this.sharedTemplateContext
            ])
            .subscribe(([siteSubInv, customer, sharedTemplateContext]) => {
                if (!sharedTemplateContext || !siteSubInv || !customer) {
                    return;
                }

                const poolerIds = sharedTemplateContext.customerAssignments.assignments
                    .filter(a => a.siteSubinventoryId === siteSubInv.id && a.customerId === customer.id)
                    .map(a => a.poolerId);

                this.setTemplateContext(row, {
                    ...row.localContext.value,
                    poolers: sharedTemplateContext.customerAssignments.customers.filter(
                        c => c.isActive && c.isPooler && poolerIds.includes(c.id)
                    )
                });
            }),
            combineLatest([
                fromFormControl(poolerControl),
                fromFormControl(customerControl),
                this.sharedTemplateContext
            ])
            .subscribe(([pooler, customer, sharedTemplateContext]) => {
                if (!sharedTemplateContext || !pooler) {
                    return;
                }

                const assignments = sharedTemplateContext.customerAssignments;
                const customerId = customer.id;
                const poolerId = pooler.id;

                const poolerAssignments = assignments.assignments
                    .filter(a => a.customerId === customerId && a.poolerId === poolerId);

                const shipToSites = pooler.customerSites.filter(site =>
                    site.activeFlag &&
                    poolerAssignments.some(a => a.poolerSiteId === site.id || !a.poolerSiteId)
                );

                const recentIds = assignments.poolerRecentShipTos?.[poolerId] ?? [];

                const options: SelectItem<CustomerSite>[] = shipToSites
                    .map(site => ({
                        site,
                        isRecent: recentIds.includes(site.id)
                    }))
                    .sort((a, b) => a.isRecent ? -1 : 1)
                    .map((s, index) => ({
                        value: s.site,
                        label: s.site.chepGlobalLocationId
                            ? `${s.site.chepGlobalLocationId} - ${s.site.partySiteName}`
                            : s.site.partySiteName,
                        styleClass: index === 0 && recentIds.length > 0
                            ? 'recent'
                            : index === recentIds.length
                                ? 'all'
                                : ''
                    }));

                this.setTemplateContext(row, {
                    ...row.localContext.value,
                    shipToLocationOptions: options
                });
            }),
            combineLatest([
                fromFormControl(siteSubInvControl),
                fromFormControl(customerControl),
                fromFormControl(poolerControl),
                fromFormControl(shipToLocationControl),
                this.productService.CurrentProducts,
            ])
            .pipe(distinctUntilChanged())
            .subscribe(([siteSubInv, customer, pooler, shipToLocation, currentProducts]) => {
                const templateContext = this.sharedTemplateContext.value;
                if (!templateContext || !pooler || !siteSubInv || !customer || !shipToLocation) {
                    return;
                }

                const productIds = templateContext.customerAssignments.assignments
                    .filter(a => a.customerId == customer.id && a.siteSubinventoryId == siteSubInv.id
                        && (a.poolerId == pooler.id || !a.poolerId)
                        && (a.poolerSiteId == shipToLocation.id || !a.poolerSiteId))
                    .flatMap(a => a.productIds);

                this.setTemplateContext(row, {
                    ...row.localContext.value,
                    products: currentProducts.filter(p => productIds.includes(p.id))
                });
            })
        ];
    }

    private addFormGroup(): Observable<FormGroup | null> {
        if (!this.selectedTemplate) {
            return of(null);
        }

        return this.buildLoadFormGroup(this.selectedTemplate)
            .pipe(
                take(1),
                tap(group => {
                    const bolNumber = group.get('propakBOLNumber')?.value;
                    if (!bolNumber) {
                        throw new Error("Missing `propakBOLNumber`");
                    }

                    const localContext = new BehaviorSubject<OutboundLoadTemplateLocalContext>({
                        customers: [],
                        poolers: [],
                        products: [],
                        shipToLocationOptions: []
                    });

                    const row: OutboundLoadRow = {
                        formGroup: group,
                        localContext,
                        subscriptions: [],
                        result: { result: null, error: null, key: bolNumber }
                    };

                    this.setUpRowSubscriptions(row);
                    this.rows.push(row);
            }));
    }

    addRow(): Promise<FormGroup> {
        return firstValueFrom(this.addFormGroup());
    }

    removeRow(index: number) {
        const [row] = this.rows.splice(index, 1);
        row.subscriptions.forEach(s => s.unsubscribe());
    }

    private handleBulkCreateResults(results: BulkCreateResult<OutboundLoad, string>[]) {
        const erroredResultsByIndex: Record<number, string> = {};
        const createdResults: string[] = [];

        results.forEach((loadResult, index) => {
            const matchingRow = this.rows.find(row =>
                row.formGroup.get('propakBOLNumber')?.value === loadResult.key
            );

            if (matchingRow) {
                matchingRow.result = loadResult;

                if(this.hasCreated(matchingRow)) {
                    matchingRow.formGroup.disable({onlySelf: true});
                    createdResults.push(`Load ${matchingRow.result.key} has been saved.`);
                }
            }

            if (!loadResult.result) {
                // NOTE(nicholas-ramsey): The API should return the results in the same order we sent them.
                // Generally avoiding that since it may change, but for errors it's probably fine.
                erroredResultsByIndex[index] = `Row ${index + 1} errored with '${loadResult.error ?? 'Unknown error'}'`;
            }
        });

        if(createdResults.length > 0) {
            this.messageService.add({
                summary: 'Loads Created',
                severity: 'success',
                data: { list: createdResults },
            });
        }

        const erroredMessages = Object.values(erroredResultsByIndex);
        if (erroredMessages.length > 0) {
            this.messageService.add({
                severity: 'error',
                summary: 'Bulk Create Errors',
                data: {
                    list: erroredMessages
                },
                sticky: true,
            });
        }
    }

    saveLoads() {
        const uncreatedRows = this.rows.filter(row => !this.hasCreated(row));
        if (!uncreatedRows.every(row => row.formGroup.valid)) {
            return;
        }

        const unsavedOutboundLoads = uncreatedRows
            .map(row =>
                this.selectedTemplateDefinition.toOutboundLoadModel(
                    row.formGroup,
                    this.sharedTemplateContext.value,
                    row.localContext.value
                )
            );
        if(unsavedOutboundLoads.length === 0) {
            return;
        }

        this.loading = true;
        this.outboundLoudService.bulkCreate(unsavedOutboundLoads).subscribe({
            next: (results) => {
                this.handleBulkCreateResults(results);
            },
            error: () => {
                this.loading = false;

                this.messageService.add({
                    severity: 'error',
                    sticky: true,
                    summary: 'Bulk Create Failed',
                });
            },
            complete: () => {
                this.loading = false;
            }
        })
    }

    hasCreated(row: OutboundLoadRow): boolean {
        return !!row?.result?.result;
    }

    hasErrored(row: OutboundLoadRow): boolean {
        return !!row?.result?.error;
    }

    onShippedShiftDateChanged(shippedDate: DateTime, index: number) {
        const group = this.rows[index].formGroup;

        group.get("dateShipped").setValue(shippedDate.toJSDate());
    }

    getExcludedTrailerIds(index: number): number[] {
        return this.rows
            .filter((_, i) => i !== index)
            .map(row => row.formGroup.get('trailerId')?.value)
            .filter((id): id is number => typeof id === 'number');
    }

    showClonePanel(event: MouseEvent, rowIndex: number) {
        this.cloningRowIndex = rowIndex;
        this.clonePanel.toggle(event);
    }

    confirmClone(times: number) {
        if(times > this.maxClones) {
            this.messageService.add({
                severity: 'warn',
                summary: `Can only clone up to ${this.maxClones} rows at once`,
            });
            times = this.maxClones;
        }
        if (this.cloningRowIndex === null || times < 1) {
            return;
        }

        const sourceGroup = this.rows[this.cloningRowIndex].formGroup;
        if (!sourceGroup || !this.selectedTemplate) {
            return;
        }

        for (let i = 0; i < times; i++) {
            this.addFormGroup().subscribe((latestGroup) => {
                const {propakBOLNumber, trailerId, ...rest} = sourceGroup.getRawValue();
                latestGroup.patchValue(rest, {onlySelf: true});
            });
        }

        this.clonePanel.hide();
        this.cloningRowIndex = null;
    }

    /**
     * Fix for Angular template not inferring the type of the context correctly.
     * @param context
     */
    asLocalContext(context: unknown): OutboundLoadTemplateLocalContext {
        return context as OutboundLoadTemplateLocalContext;
    }

    printLoad(loadRow: OutboundLoadRow) {
        if(!loadRow.result) {
            return;
        }

        this.bolService.DownloadBolPdf(loadRow.result?.result.id);
    }
}
